import { deburr, get, isEmpty } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SidePanel from 'src/components/design-system/SidePanel';
import { handlePostMessage } from 'src/hooks/usePostMessage';
import { useProductInformationPanel } from 'src/hooks/useProductInformationPanel';
import useWindowSize from 'src/hooks/useWindowSize';
import EditItemInformationDialog from 'src/sections/catalog_products/EditItemInformationDialog';
import EnlargedImageView from 'src/sections/catalog_products/EnlargedImageView';
import ExpandedDescriptionView from 'src/sections/catalog_products/ExpandedDescriptionView';
import { ProductData } from 'src/sections/catalog_products/useEditItemInformationForm';
import { useContextSelector } from 'use-context-selector';
import { ShoppingCartContext } from './ShoppingCartContext';
import ShoppingCartProductInformationView from './ShoppingCartProductInformationView';

interface ShoppingCartProductInformationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ShoppingCartProductInformationPanel({
  isOpen,
  onClose,
}: ShoppingCartProductInformationPanelProps) {
  const { t } = useTranslation();
  const { height, width } = useWindowSize();
  const {
    isImageEnlarged,
    isDescriptionExpanded,
    handleImageClick,
    handleCloseEnlargedImage,
    handleReadMoreClick,
    handleCloseDescription,
  } = useProductInformationPanel();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const editDialogCancelRef = useRef<(() => void) | null>(null);

  const updateProductSelection = useContextSelector(
    ShoppingCartContext,
    (context) => context.updateProductSelection
  );
  const setSelectedProduct = useContextSelector(
    ShoppingCartContext,
    (context) => context.setSelectedProduct
  );
  const loadingProductDetails = useContextSelector(
    ShoppingCartContext,
    (context) => context.loadingProductDetails
  );
  const productDetails = useContextSelector(
    ShoppingCartContext,
    (context) => context.productDetails
  );
  const selectedProduct = useContextSelector(
    ShoppingCartContext,
    (context) => context.selectedProduct
  );
  const fetchProductDetails = useContextSelector(
    ShoppingCartContext,
    (context) => context.fetchProductDetails
  );
  const fetchProducts = useContextSelector(
    ShoppingCartContext,
    (context) => context.fetchProducts
  );
  const productSelections = useContextSelector(
    ShoppingCartContext,
    (context) => context.productSelections
  );
  const isAddingToFavorites = useContextSelector(
    ShoppingCartContext,
    (context) => context.isAddingToFavorites
  );
  const setIsSwapItemModalOpen = useContextSelector(
    ShoppingCartContext,
    (context) => context.setIsSwapItemModalOpen
  );
  const setSwapItemData = useContextSelector(
    ShoppingCartContext,
    (context) => context.setSwapItemData
  );
  const folderOptions = useContextSelector(
    ShoppingCartContext,
    (context) => context.folderOptions
  );
  const handleAddToFavorites = useContextSelector(
    ShoppingCartContext,
    (context) => context.handleAddToFavorites
  );
  const getSelectedProductCatalogType = useContextSelector(
    ShoppingCartContext,
    (context) => context.getSelectedProductCatalogType
  );

  const setQuantity = useCallback(
    (newQty: number) => {
      if (selectedProduct?.productKey) {
        updateProductSelection(selectedProduct.productKey, {
          qty: newQty,
          selected: newQty !== 0,
        });
      }
    },
    [selectedProduct?.productKey, updateProductSelection]
  );

  const { qty: quantity } = useMemo(
    () =>
      (selectedProduct?.productKey ? productSelections[selectedProduct.productKey] : null) || {
        selected: false,
        qty: 0,
      },
    [productSelections, selectedProduct?.productKey]
  );

  const getPanelTitle = useMemo(() => {
    if (isEditDialogOpen) {
      return t('Edit Item Information');
    }
    if (isDescriptionExpanded) {
      return t('Detailed Description');
    }
    if (isImageEnlarged) {
      return t('Item Image');
    }
    return t('Item Information');
  }, [isEditDialogOpen, isDescriptionExpanded, isImageEnlarged, t]);

  const handleEdit = useCallback(() => {
    setIsEditDialogOpen(true);
  }, []);

  const handleEditDialogClose = useCallback(() => {
    setIsEditDialogOpen(false);
  }, []);

  const handlePanelClose = useCallback(() => {
    if (isEditDialogOpen) {
      if (editDialogCancelRef.current) {
        editDialogCancelRef.current();
      } else {
        handleEditDialogClose();
      }
      return;
    }
    if (isDescriptionExpanded) {
      handleCloseDescription();
    } else if (isImageEnlarged) {
      handleCloseEnlargedImage();
    } else if (!loadingProductDetails) {
      setSelectedProduct(null);
      onClose();
    }
  }, [
    isEditDialogOpen,
    isDescriptionExpanded,
    isImageEnlarged,
    handleEditDialogClose,
    handleCloseDescription,
    handleCloseEnlargedImage,
    loadingProductDetails,
    setSelectedProduct,
    onClose,
  ]);

  const productDataForEdit: ProductData | null = useMemo(() => {
    if (!productDetails) return null;
    return {
      title: deburr(get(productDetails, 'description', '')).trim(),
      price: productDetails.price?.toString() || '',
      description: deburr(get(productDetails, 'info', '')).trim(),
      imageUrl: productDetails.url || '',
      productDetails: {
        'Item Name': deburr(get(productDetails, 'description', '')).trim(),
        Price: productDetails.price?.toString() || '',
        UPC: productDetails.upc || '',
        UNSPSC: productDetails.unspc || '',
      },
      manufacturingAndPackaging: {
        'Mfg Name': deburr(get(productDetails, 'manufacturerName', '')).trim(),
        'Mfg #': productDetails.manufacturerNumber || '',
        'Brand Name': deburr(get(productDetails, 'brandName', '')).trim(),
        UOM: productDetails.uom || '',
        'Case Pack Units': productDetails.casePack?.toString() || '',
        Weight: productDetails.weight || '',
      },
    };
  }, [productDetails]);

  const handleEditSaveSuccess = useCallback(async () => {
    await fetchProductDetails();
    await fetchProducts();
    handlePostMessage({ cartUpdated: true });
  }, [fetchProductDetails, fetchProducts]);

  const handleSwapItemClick = useCallback(() => {
    if (!productDetails || !selectedProduct) return;
    setIsSwapItemModalOpen(true);
    setSwapItemData({
      pageType: 1,
      orderBinId: selectedProduct.clientOrderBinId,
      supplierId: productDetails.supplierId,
      supplierCompanyId: productDetails.supplierCompanyId,
      itemNumber: productDetails.itemNum,
      productId: productDetails.productId,
      catalogId: productDetails.catalogId,
      categoryId: productDetails.categoryId,
      quantity,
      price: Number(productDetails.price),
      uom: productDetails.uom,
    });
    onClose();
  }, [
    productDetails,
    quantity,
    selectedProduct,
    setIsSwapItemModalOpen,
    setSwapItemData,
    onClose,
  ]);

  return (
    <SidePanel
      title={getPanelTitle}
      isOpen={isOpen}
      onClose={handlePanelClose}
      width={width < 900 ? `${width}px` : '900px'}
      position="right"
      className={`z-[1000] bg-gray-100 ${
        width < 900 ? 'pb-[16px]' : 'pb-0'
      } absolute top-0 right-0 z-[1000]`}
      titleClassName="bg-[#0E80B4] px-[32px] -mx-[32px] text-white -mt-[24px] py-[8px] [&_button:focus-visible]:!outline [&_button:focus-visible]:!outline-2 [&_button:focus-visible]:!outline-offset-[-2px] [&_button:focus-visible]:!outline-white"
    >
      <div className="bg-gray-100" style={{ height: `${height - 100}px` }}>
        {isEditDialogOpen && productDataForEdit ? (
          <EditItemInformationDialog
            isOpen={isEditDialogOpen}
            onClose={handleEditDialogClose}
            productData={productDataForEdit}
            selectedProduct={selectedProduct}
            onSaveSuccess={handleEditSaveSuccess}
            onRequestClose={(cancelHandler) => {
              editDialogCancelRef.current = cancelHandler;
            }}
          />
        ) : loadingProductDetails ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-gray-500">{t('Loading item details...')}</div>
          </div>
        ) : isEmpty(productDetails) ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-gray-500">{t('No item selected')}</div>
          </div>
        ) : isDescriptionExpanded ? (
          <ExpandedDescriptionView
            description={get(productDetails, 'info', '') as string}
            height={height}
            onClose={handleCloseDescription}
          />
        ) : isImageEnlarged ? (
          <EnlargedImageView
            imageUrl={get(productDetails, 'linkUrl', '') as string}
            title={get(productDetails, 'imageDescription', '') as string}
            height={height}
            onClose={handleCloseEnlargedImage}
          />
        ) : !isEmpty(productDetails) ? (
          <ShoppingCartProductInformationView
            productData={productDetails}
            catalogType={getSelectedProductCatalogType()}
            quantity={quantity}
            onQuantityChange={setQuantity}
            onImageClick={handleImageClick}
            onReadMoreClick={handleReadMoreClick}
            onAddToFavorites={handleAddToFavorites}
            onEdit={handleEdit}
            onSwapItemClick={handleSwapItemClick}
            isLoading={loadingProductDetails || isAddingToFavorites}
            folderOptions={folderOptions}
          />
        ) : null}
      </div>
    </SidePanel>
  );
}
